import { createAuth0Client } from "@auth0/auth0-spa-js";

let _auth0Client;

export const navigationGuard = async (to, from, next) => {
  // Allow routes that don't require authentication
  if (to.matched.some(record => record.meta.noAuth)) {
    next();
    return;
  }

  // Wait for client to be initialized
  if (!_auth0Client) {
    console.warn("Auth0 client not initialized yet, redirecting to login");
    next({ path: "/" });
    return;
  }

  try {
    const isAuthenticated = await _auth0Client.isAuthenticated();
    if (isAuthenticated) {
      next();
    } else {
      next({ path: "/" });
    }
  } catch (error) {
    console.error("Error in navigation guard:", error);
    next({ path: "/" });
  }
};

export const getCurrentUser = async () => {
  if (!_auth0Client) {
    console.warn("Auth0 client not initialized");
    return null;
  }

  try {
    if (await _auth0Client.isAuthenticated()) {
      return await _auth0Client.getUser();
    }
  } catch (error) {
    console.error("Error getting current user:", error);
  }
  return null;
};

function install(
  Vue,
  {
    domain,
    clientId,
    redirectUri,
    audience,
    scope,
    cacheLocation,
    useRefreshTokens,
    logoutRedirectUri
  }
) {
  const auth = {};

  auth.init = async () => {
    const authParams = {
      redirect_uri: redirectUri,
      // `offline_access` is required for Auth0 to issue a refresh token; without
      // it `useRefreshTokens` has nothing to refresh with and silent auth falls
      // back to the iframe/session-cookie flow that throws `consent_required`.
      scope: scope || "openid profile email offline_access"
    };

    // Only add audience if it's provided
    if (audience) {
      authParams.audience = audience;
    }

    _auth0Client = await createAuth0Client({
      domain,
      clientId,
      authorizationParams: authParams,
      // Use refresh tokens (with rotation, configured in the Auth0 dashboard)
      // for renewing the access token instead of silent iframe authentication.
      // This is what keeps the session alive past the ~10 min mark and avoids
      // `consent_required`. Defaults to true unless explicitly disabled.
      useRefreshTokens: useRefreshTokens !== false,
      // The refresh token must be persisted across reloads, so it has to live
      // in localStorage rather than in memory.
      cacheLocation: cacheLocation || "localstorage"
    });
  };

  // Expose the underlying Auth0 SPA client so Vuex actions (token-expiration
  // check, manual refresh) can call `getTokenSilently` / `logout` on it.
  // Returns undefined until `init()` has resolved.
  auth.getClient = () => _auth0Client;

  auth.signIn = async () => {
    if (!_auth0Client) {
      throw new Error("Auth0 client not initialized");
    }
    await _auth0Client.loginWithPopup({
      authorizationParams: {
        redirect_uri: redirectUri
      }
    });
    const user = await _auth0Client.getUser();
    const isAuthenticated = await _auth0Client.isAuthenticated();
    return { user, isAuthenticated };
  };

  auth.signOut = async () => {
    if (!_auth0Client) {
      throw new Error("Auth0 client not initialized");
    }
    await _auth0Client.logout({
      logoutParams: {
        returnTo: logoutRedirectUri
      }
    });
  };

  auth.handleLoginRedirect = async () => {
    if (!_auth0Client) {
      console.warn("Auth0 client not initialized, cannot handle redirect");
      return;
    }
    const query = window.location.search;
    if (query.includes("code=") && query.includes("state=")) {
      try {
        await _auth0Client.handleRedirectCallback();
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname
        );
      } catch (e) {
        console.error("login redirection failed cause", e);
      }
    }
  };

  auth.isAuthenticated = async () => {
    if (!_auth0Client) {
      return false;
    }
    return await _auth0Client.isAuthenticated();
  };

  auth.getUser = async () => {
    if (!_auth0Client) {
      return null;
    }
    if (await _auth0Client.isAuthenticated()) {
      return await _auth0Client.getUser();
    } else {
      return null;
    }
  };

  auth.getTokens = async () => {
    if (!_auth0Client) {
      throw new Error("Auth0 client not initialized");
    }
    try {
      const accessToken = await _auth0Client.getTokenSilently();
      const idToken = await _auth0Client.getIdTokenClaims();
      return {
        idToken: idToken?.__raw,
        accessToken
      };
    } catch (error) {
      console.error("Failed to get tokens, likely expired:", error);
      // Clear auth state and redirect to login
      localStorage.clear();
      sessionStorage.clear();
      window.location.href = "/login";
      throw error;
    }
  };

  auth.handleSignInWithRedirect = async () => {
    if (!_auth0Client) {
      console.warn("Auth0 client not initialized");
      return;
    }
    const query = window.location.search;
    if (query.includes("code=") && query.includes("state=")) {
      try {
        await _auth0Client.handleRedirectCallback();
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname
        );
      } catch (e) {
        console.error("handleRedirectCallback failed cause", e);
      }
    } else if (!(await _auth0Client.isAuthenticated())) {
      await _auth0Client.loginWithRedirect({
        authorizationParams: {
          redirect_uri: redirectUri
        }
      });
    }
  };

  Vue.prototype.$auth = auth;

  console.log("init-auth0-auth");
}

export default { install };
